import React from 'react';
import { Button, Badge } from 'components/ui';
import {
  UserIcon,
  CalendarDaysIcon,
  CalendarIcon,
  PencilSquareIcon,
  PaperClipIcon,
  TicketIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  CreditCardIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  VideoCameraIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ClockIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';
import { hasPermission } from 'utils/permissionsUtils';

const SidebarNavigation = ({ selectedTab, setSelectedTab, isMobile, showMobileSidebar, setShowMobileSidebar, selectedContentTab, setSelectedContentTab, entityName = "Lead", leadData = null }) => {
  const [expandedLeadDetails, setExpandedLeadDetails] = React.useState(selectedTab === 'leads-tab');
  const prevSelectedTab = React.useRef(selectedTab);

  // Auto-expand lead details only when switching TO leads-tab from another tab
  React.useEffect(() => {
    // Only expand when switching from a different tab to leads-tab
    if (selectedTab === 'leads-tab' && prevSelectedTab.current !== 'leads-tab') {
      setExpandedLeadDetails(true);
    }
    prevSelectedTab.current = selectedTab;
  }, [selectedTab]);

  const tabs = [
    { id: 'leads-tab', name: `${entityName} Profile`, icon: <UserIcon className="w-5 h-5" />, permission: "MLLV", hasSubTabs: true },
    { id: 'timeline', name: `${entityName} Timeline`, icon: <CalendarDaysIcon className="w-5 h-5" />, permission: "MLLV" },
    // { id: 'calendar', name: 'Event Scheduler', subtitle: '(Calendar)', icon: <CalendarIcon className="w-5 h-5" />, permission: "FUPSVFUP" },
    // { id: 'call-history', name: 'Call History', icon: <PhoneIcon className="w-5 h-5" /> },  // Removed - functionality moved to CallModal
    { id: 'notes', name: 'Notes', icon: <PencilSquareIcon className="w-5 h-5" />, permission: "MLLV" },
    { id: 'communications', name: 'Communication Activity', icon: <ChatBubbleLeftRightIcon className="w-5 h-5" />, permission: "MLLV" },  // Removed - not implemented
    { id: 'documents', name: 'Document Manager', icon: <PaperClipIcon className="w-5 h-5" />, permission: "MLLV" },
    { id: 'tickets', name: 'Raise Query', icon: <TicketIcon className="w-5 h-5" />, permission: "QMQV" }
  ];

  const allLeadDetailsSubTabs = [
    { id: 'lead-details', name: `${entityName} Details`, icon: <ChartBarIcon className="w-4 h-4" /> },
    { id: 'offer-letter', name: 'Offer Letter', icon: <DocumentTextIcon className="w-4 h-4" /> },
    { id: 'acceptance-letter', name: 'Acceptance Letter', icon: <DocumentTextIcon className="w-4 h-4" /> },
    { id: 'payment-details', name: 'Payment Details', icon: <CreditCardIcon className="w-4 h-4" /> },
    { id: 'follow-up-calendar', name: 'Follow Up Calendar', icon: <ClockIcon className="w-4 h-4" /> },
    { id: 'application-form', name: 'Application Form', icon: <DocumentTextIcon className="w-4 h-4" /> },
    { id: 'google-meet', name: 'Google Meet', icon: <VideoCameraIcon className="w-4 h-4" /> },
  ];

  // Filter sub-tabs based on lead type
  const leadDetailsSubTabs = React.useMemo(() => {
    let tabs = allLeadDetailsSubTabs;
    // Hide offer-letter and acceptance-letter tab for non-international leads
    if (leadData && !leadData.IsInternationalLead) {
      tabs = tabs.filter(tab => tab.id !== 'offer-letter' && tab.id !== 'acceptance-letter');
    }

    // Hide acceptance-letter tab if offer letter is not generated yet
    const offerLetter = leadData?.offerLetter || null;
    const hasOfferLetter = !!(offerLetter && (offerLetter.FilePath || offerLetter.FileName || offerLetter.Id));
    if (!hasOfferLetter) {
      tabs = tabs.filter(tab => tab.id !== 'acceptance-letter');
    }
    
    // Fee Payment requires Accounts User assigned, but the tab should be visible right away 
    // and disabled/blocked based on assignment state.
    // Given the prompt "activate that is fee payment link and a option for it should be tehre in sidebar",
    // we make it visible in the sidebar always, but inside we handle the lock state, OR we conditionally render it only when we can pay.
    // We will leave it visible for now, and handle logic in the fee-payment view.
    return tabs;
  }, [leadData, entityName]);


  const handleMainTabClick = (tabId) => {
    setSelectedTab(tabId);
    // Reset content tab when switching to main tabs other than leads-tab
    if (tabId !== 'leads-tab') {
      setSelectedContentTab('lead-details');
      setExpandedLeadDetails(false);
    }
    if (isMobile) {
      setShowMobileSidebar(false);
    }
  };

  const handleLeadDetailsClick = () => {
    // Only toggle expand/collapse, don't change the selected content tab or main tab
    setExpandedLeadDetails(!expandedLeadDetails);
  };

  const handleSubTabClick = (subTabId) => {
    setSelectedTab('leads-tab');
    setSelectedContentTab(subTabId);
    if (isMobile) {
      setShowMobileSidebar(false);
    }
  };

  if (isMobile) {
    return (
      <>
        {/* Mobile Navigation */}
        {showMobileSidebar && (
          <div className="fixed inset-0 z-40 bg-gray-900/50 bg-opacity-50" onClick={() => setShowMobileSidebar(false)} />
        )}
        <div className={`
          ${showMobileSidebar ? 'translate-x-0' : '-translate-x-full'}
          fixed left-0 top-0 z-50 h-full w-64 bg-white dark:bg-dark-800 shadow-lg transition-transform duration-300 ease-in-out
          md:relative md:translate-x-0 md:shadow-none md:z-auto
        `}>
          <div className="p-4 border-b border-gray-200 dark:border-dark-600">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-gray-500 dark:text-dark-400 uppercase tracking-wider">
                {entityName} Management
              </h3>
              <Badge variant="soft" color="info" className="text-xs">
                {tabs.length}
              </Badge>
            </div>
          </div>
          <div className="p-4 overflow-y-auto h-full">
            <div className="space-y-1">
              {tabs.map((tab) => {
                if (!hasPermission(tab.permission)) {
                  return null;
                }

                if (tab.id === 'leads-tab') {
                  return (
                    <div key={tab.id}>
                      <Button
                        onClick={handleLeadDetailsClick}
                        variant={selectedTab === 'leads-tab' ? 'soft' : 'flat'}
                        color={selectedTab === 'leads-tab' ? 'neutral' : 'neutral'}
                        className={`flex items-center gap-3 w-full justify-start px-3 py-3 text-sm transition-all duration-200 ${selectedTab === 'leads-tab'
                          ? 'bg-gray-100 dark:bg-dark-700/50'
                          : 'hover:translate-x-1'
                          }`}
                        unstyled={false}
                      >
                        <span className="w-5 h-5 shrink-0">{tab.icon}</span>
                        <div className="text-left flex-1">
                          <div className="font-medium">{tab.name}</div>
                          {tab.subtitle && (
                            <div className="text-xs opacity-75">
                              {tab.subtitle}
                            </div>
                          )}
                        </div>
                        {expandedLeadDetails ? (
                          <ChevronDownIcon className="w-4 h-4" />
                        ) : (
                          <ChevronRightIcon className="w-4 h-4" />
                        )}
                      </Button>

                      {expandedLeadDetails && (
                        <div className="ml-4 space-y-1 mt-1">
                          {leadDetailsSubTabs.map((subTab) => (
                            <Button
                              key={subTab.id}
                              onClick={() => handleSubTabClick(subTab.id)}
                              variant={selectedTab === 'leads-tab' && selectedContentTab === subTab.id ? 'soft' : 'flat'}
                              color={selectedTab === 'leads-tab' && selectedContentTab === subTab.id ? 'primary' : 'neutral'}
                              className={`flex items-center gap-3 w-full justify-start px-3 py-2 text-sm transition-all duration-200 ${selectedTab === 'leads-tab' && selectedContentTab === subTab.id
                                ? 'shadow-sm bg-primary-50 dark:bg-primary-900/20'
                                : 'hover:translate-x-1'
                                }`}
                              unstyled={false}
                            >
                              <span className="w-4 h-4 shrink-0">{subTab.icon}</span>
                              <div className="text-left">
                                <div className="font-medium text-sm">{subTab.name}</div>
                              </div>
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <Button
                    key={tab.id}
                    onClick={() => handleMainTabClick(tab.id)}
                    variant={selectedTab === tab.id ? 'soft' : 'flat'}
                    color={selectedTab === tab.id ? 'primary' : 'neutral'}
                    className={`flex items-center gap-3 w-full justify-start px-3 py-3 text-sm transition-all duration-200 ${selectedTab === tab.id
                      ? 'shadow-sm'
                      : 'hover:translate-x-1'
                      }`}
                    unstyled={false}
                  >
                    <span className="w-5 h-5 shrink-0">{tab.icon}</span>
                    <div className="text-left">
                      <div className="font-medium">{tab.name}</div>
                      {tab.subtitle && (
                        <div className="text-xs opacity-75">
                          {tab.subtitle}
                        </div>
                      )}
                    </div>
                  </Button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Mobile Tab Bar at Bottom */}
        <div className="border-b border-gray-200 dark:border-dark-600 bg-gray-50/50 dark:bg-dark-800/50">
          <div className="overflow-x-auto">
            <div className="flex">
              {tabs.slice(0, 4).map((tab) => (
                <Button
                  key={tab.id}
                  onClick={() => handleMainTabClick(tab.id)}
                  variant="flat"
                  color={selectedTab === tab.id ? 'primary' : 'neutral'}
                  className={`flex flex-col items-center gap-1 px-3 py-2 text-xs border-b-2 transition-all duration-200 whitespace-nowrap min-w-0 flex-1 ${selectedTab === tab.id
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-transparent text-gray-500 dark:text-dark-300 hover:text-gray-700 dark:hover:text-dark-200'
                    }`}
                  unstyled={true}
                >
                  <span className="w-4 h-4 shrink-0">{React.cloneElement(tab.icon, { className: 'w-4 h-4' })}</span>
                  <span className="truncate text-xs">
                    {tab.name.split(' ')[0]}
                  </span>
                </Button>
              ))}
              {tabs.length > 4 && (
                <Button
                  onClick={() => setShowMobileSidebar(true)}
                  variant="flat"
                  color="neutral"
                  className="flex flex-col items-center gap-1 px-3 py-2 text-xs border-b-2 border-transparent text-gray-500 dark:text-dark-300 hover:text-gray-700 dark:hover:text-dark-200 whitespace-nowrap min-w-0 flex-1"
                  unstyled={true}
                >
                  <span className="text-lg">⋯</span>
                  <span className="text-xs">More</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // Desktop Navigation - Professional Design
  return (
    <div className="w-64 border-r border-gray-200/60 dark:border-dark-600/60 bg-white dark:bg-dark-800">
      <div className="p-5">
        {/* Professional Header */}
        <div className="mb-6 pb-4 border-b border-gray-200 dark:border-dark-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-1 h-7 bg-gradient-to-b from-primary-600 to-primary-700 rounded-full"></div>
              <h3 className="text-[11px] font-bold text-gray-800 dark:text-dark-100 uppercase tracking-[0.08em]">
                {entityName} Management
              </h3>
            </div>
            <Badge
              variant="soft"
              color="info"
              className="text-[10px] font-semibold px-2 py-0.5"
            >
              {tabs.length}
            </Badge>
          </div>
        </div>

        <nav className="space-y-1">
          {tabs.map((tab) => {
            if (tab.id === 'leads-tab') {
              return (
                <div key={tab.id}>
                  {/* Main Lead Tab */}
                  <Button
                    onClick={handleLeadDetailsClick}
                    variant="flat"
                    color="neutral"
                    className={`
                      group relative flex items-center gap-3 w-full justify-start px-3 py-2.5 text-sm 
                      transition-all duration-200 rounded-lg
                      ${selectedTab === 'leads-tab'
                        ? 'bg-primary-50 dark:bg-primary-950/20 text-primary-700 dark:text-primary-400 shadow-sm'
                        : 'text-gray-700 dark:text-dark-300 hover:bg-gray-50 dark:hover:bg-dark-700/50'
                      }
                    `}
                    unstyled={true}
                  >
                    {/* Icon */}
                    <div className={`
                      w-8 h-8 rounded-lg flex items-center justify-center shrink-0
                      transition-all duration-200
                      ${selectedTab === 'leads-tab'
                        ? 'bg-gradient-to-br from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/25'
                        : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-dark-400 group-hover:bg-primary-50 group-hover:text-primary-600 dark:group-hover:bg-primary-950/30 dark:group-hover:text-primary-400'
                      }
                    `}>
                      {React.cloneElement(tab.icon, { className: 'w-4 h-4', strokeWidth: 2 })}
                    </div>

                    <div className="text-left flex-1">
                      <div className={`font-semibold text-[13px] leading-tight ${selectedTab === 'leads-tab'
                        ? 'text-primary-700 dark:text-primary-400'
                        : 'text-gray-700 dark:text-dark-200 group-hover:text-gray-900 dark:group-hover:text-dark-100'
                        }`}>
                        {tab.name}
                      </div>
                    </div>

                    {/* Chevron */}
                    <div className={`
                      w-4 h-4 shrink-0 transition-transform duration-200
                      ${selectedTab === 'leads-tab' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-dark-500'}
                    `}>
                      {expandedLeadDetails ? (
                        <ChevronDownIcon className="w-4 h-4" strokeWidth={2.5} />
                      ) : (
                        <ChevronRightIcon className="w-4 h-4" strokeWidth={2.5} />
                      )}
                    </div>
                  </Button>

                  {/* Sub-navigation */}
                  {expandedLeadDetails && (
                    <div className="mt-1 ml-3 pl-3 space-y-0.5 border-l-2 border-gray-200 dark:border-dark-700">
                      {leadDetailsSubTabs.map((subTab, index) => (
                        <Button
                          key={subTab.id}
                          onClick={() => handleSubTabClick(subTab.id)}
                          variant="flat"
                          color="neutral"
                          className={`
                            group relative flex items-center gap-2.5 w-full justify-start px-2.5 py-2 text-sm
                            transition-all duration-200 rounded-md
                            ${selectedTab === 'leads-tab' && selectedContentTab === subTab.id
                              ? 'bg-primary-50 dark:bg-primary-950/20 text-primary-700 dark:text-primary-400 shadow-sm'
                              : 'text-gray-600 dark:text-dark-400 hover:bg-gray-50 dark:hover:bg-dark-700/30 hover:text-gray-900 dark:hover:text-dark-200'
                            }
                          `}
                          unstyled={true}
                        >
                          {/* Icon badge */}
                          <div className={`
                            w-6 h-6 rounded-md flex items-center justify-center shrink-0
                            transition-all duration-200
                            ${selectedTab === 'leads-tab' && selectedContentTab === subTab.id
                              ? 'bg-primary-600 text-white shadow-sm'
                              : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-dark-500 group-hover:bg-primary-50 group-hover:text-primary-600 dark:group-hover:bg-primary-950/30 dark:group-hover:text-primary-400'
                            }
                          `}>
                            {React.cloneElement(subTab.icon, { className: 'w-3.5 h-3.5', strokeWidth: 2 })}
                          </div>

                          <div className="text-left text-[13px] font-medium leading-tight">
                            {subTab.name}
                          </div>

                          {/* Active indicator */}
                          {selectedTab === 'leads-tab' && selectedContentTab === subTab.id && (
                            <div className="ml-auto w-1 h-1 rounded-full bg-primary-600 dark:bg-primary-500"></div>
                          )}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Button
                key={tab.id}
                onClick={() => handleMainTabClick(tab.id)}
                variant="flat"
                color="neutral"
                className={`
                  group relative flex items-center gap-3 w-full justify-start px-3 py-2.5 text-sm 
                  transition-all duration-200 rounded-lg
                  ${selectedTab === tab.id
                    ? 'bg-primary-50 dark:bg-primary-950/20 text-primary-700 dark:text-primary-400 shadow-sm'
                    : 'text-gray-700 dark:text-dark-300 hover:bg-gray-50 dark:hover:bg-dark-700/50'
                  }
                `}
                unstyled={true}
              >
                {/* Icon */}
                <div className={`
                  w-8 h-8 rounded-lg flex items-center justify-center shrink-0
                  transition-all duration-200
                  ${selectedTab === tab.id
                    ? 'bg-gradient-to-br from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/25'
                    : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-dark-400 group-hover:bg-primary-50 group-hover:text-primary-600 dark:group-hover:bg-primary-950/30 dark:group-hover:text-primary-400'
                  }
                `}>
                  {React.cloneElement(tab.icon, { className: 'w-4 h-4', strokeWidth: 2 })}
                </div>

                <div className="text-left">
                  <div className={`font-semibold text-[13px] leading-tight ${selectedTab === tab.id
                    ? 'text-primary-700 dark:text-primary-400'
                    : 'text-gray-700 dark:text-dark-200 group-hover:text-gray-900 dark:group-hover:text-dark-100'
                    }`}>
                    {tab.name}
                  </div>
                  {tab.subtitle && (
                    <div className={`text-[11px] mt-0.5 ${selectedTab === tab.id
                      ? 'text-primary-600/70 dark:text-primary-400/70'
                      : 'text-gray-500 dark:text-dark-400'
                      }`}>
                      {tab.subtitle}
                    </div>
                  )}
                </div>
              </Button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default SidebarNavigation;
